import { useForm } from "@tanstack/react-form";
import { Alert } from "../../../components/alert/Alert";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
} from "../../../components/modal/Dialog";
import { apiV2AssertError } from "../../../hooks/api/v2/apiV2Request";
import { usePriceTierContainerDiscountCreate } from "../../../hooks/api/v2/priceTierContainerDiscount/usePriceTierContainerDiscountCreate";
import { z } from "zod";
import { useEffect, useState } from "react";
import {
  Button,
  InputContainer,
  InputOption,
  InputSelect,
  InputNumber,
  InputCurrency,
} from "@sellar-io/components";
import { InputLabel } from "@sellar-io/components";
import { formatPriceInputToApi } from "../../../hooks/usePriceFormat";
import { usePriceTierUpdateDerived } from "../../../hooks/api/v2/priceTier/usePriceTierUpdateDerived";
import {
  containerTypeOptions,
  containerTypeValues,
  ContainerDiscountContainerType,
} from "./containerDiscountOptions";

export const ContainerDiscountCreateDialog = ({
  active,
  setActive,
  priceListId,
  activeContainers,
}: {
  active: boolean;
  setActive: (active: boolean) => void;
  priceListId: number;
  activeContainers?: Array<"any" | ContainerDiscountContainerType>;
}) => {
  const [errorMessage, setErrorMessage] = useState("");
  const onDismiss = () => {
    setActive(false);
  };
  const formSchema = z.object({
    derivationType: z.enum(["flat", "percentage"]),
    derivationValue: z.number().min(0),
    containerType: z.enum(["any", ...containerTypeValues]),
  });
  const form = useForm({
    defaultValues: {
      derivationType: "percentage",
      derivationValue: 0,
      containerType: "any",
    },
    onSubmit: ({ value }) => {
      handleCreateContainerDiscount(formSchema.parse(value));
    },
  });

  const priceTierContainerDiscountCreateMutation =
    usePriceTierContainerDiscountCreate();

  const updateDerivedPriceList = usePriceTierUpdateDerived(priceListId);

  const handleCreateContainerDiscount = ({
    derivationType,
    derivationValue,
    containerType,
  }: z.infer<typeof formSchema>) => {
    setErrorMessage("");

    if (containerType === "any") {
      updateDerivedPriceList.mutate(
        {
          derivationType,
          derivationValue,
        },
        {
          onSuccess: () => {
            onDismiss();
          },
          onError: (error) => {
            const message =
              apiV2AssertError(error)?.response?.data.message ??
              "Unable to create container discount";
            setErrorMessage(message);
          },
        },
      );
    } else {
      priceTierContainerDiscountCreateMutation.mutate(
        {
          PriceTierId: priceListId,
          derivationType,
          derivationValue,
          containerType,
        },
        {
          onSuccess: () => {
            onDismiss();
          },
          onError: (error) => {
            const message =
              apiV2AssertError(error)?.response?.data.message ??
              "Unable to create container discount";
            setErrorMessage(message);
          },
        },
      );
    }
  };

  useEffect(() => {
    const containerType =
      activeContainers && activeContainers.length > 0
        ? (["any", ...containerTypeValues] as const).find(
            (x) => !activeContainers.includes(x),
          )
        : "any";

    form.setFieldValue("containerType", containerType ?? "any");
  }, [activeContainers, form]);

  useEffect(() => {
    form.setFieldValue("derivationValue", 0);
  }, [priceListId, form]);

  return (
    <Dialog active={active} onDismiss={onDismiss}>
      <DialogNavigation
        title="Add top level discount"
        description="Any product item with their price rule set to 'Inherit' will have this discount applied"
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={onDismiss}
      />
      <DialogContent>
        {errorMessage && (
          <Alert
            type="error"
            message={
              <span className="relative -top-0.5 text-sm">{errorMessage}</span>
            }
            className="w-full text-sm"
          />
        )}
        <form
          onSubmit={(event) => {
            event.preventDefault();
            form.handleSubmit();
          }}
          className="flex flex-col gap-4"
        >
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <form.Field name="derivationType">
                {({ state, handleBlur, handleChange }) => {
                  return (
                    <div className="flex flex-col gap-1">
                      <InputLabel>Discount type</InputLabel>
                      <InputContainer>
                        <InputSelect
                          defaultValue={state.value}
                          onBlur={handleBlur}
                          onChange={(e) => {
                            const newType = e.target.value as
                              | "percentage"
                              | "flat";
                            handleChange(newType);

                            // Reset the derivationValue field appropriately
                            if (newType === "percentage") {
                              form.setFieldValue("derivationValue", 0); // reset or keep valid
                            } else if (newType === "flat") {
                              form.setFieldValue("derivationValue", 0); // or maybe keep same logic
                            }
                          }}
                        >
                          <InputOption value="percentage">
                            Percentage
                          </InputOption>
                          <InputOption value="flat">Flat</InputOption>
                        </InputSelect>
                      </InputContainer>
                    </div>
                  );
                }}
              </form.Field>
              <form.Field name="derivationValue">
                {({ state, handleBlur, handleChange }) => {
                  return (
                    <div className="flex flex-col gap-1">
                      <InputLabel>
                        Discount amount{" "}
                        {form.getFieldValue("derivationType") === "percentage"
                          ? "(%)"
                          : "(£)"}
                      </InputLabel>
                      <InputContainer>
                        {form.getFieldValue("derivationType") ===
                          "percentage" && (
                          <>
                            <span>%</span>
                            <InputNumber
                              defaultValue={state.value}
                              onBlur={handleBlur}
                              min={0}
                              max={100}
                              step={0.1}
                              onChange={(e) =>
                                handleChange(parseFloat(e.target.value))
                              }
                            />
                          </>
                        )}
                        {form.getFieldValue("derivationType") === "flat" && (
                          <InputCurrency
                            showPrefix
                            allowNegative
                            defaultValue={state.value}
                            onBlur={handleBlur}
                            onChange={(e) =>
                              handleChange(
                                formatPriceInputToApi(e.target.value) ?? 0,
                              )
                            }
                          />
                        )}
                      </InputContainer>
                    </div>
                  );
                }}
              </form.Field>
              <form.Field name="containerType">
                {({ state, handleBlur, handleChange }) => {
                  return (
                    <div className="flex flex-col gap-1">
                      <InputLabel>Container type</InputLabel>
                      <InputContainer>
                        <InputSelect
                          defaultValue={state.value}
                          onBlur={handleBlur}
                          onChange={(e) => handleChange(e.target.value)}
                        >
                          {!activeContainers?.includes("any") && (
                            <InputOption value="any">Any</InputOption>
                          )}
                          {containerTypeOptions.map(({ value, label }) =>
                            activeContainers?.includes(value) ? null : (
                              <InputOption key={value} value={value}>
                                {label}
                              </InputOption>
                            ),
                          )}
                        </InputSelect>
                      </InputContainer>
                    </div>
                  );
                }}
              </form.Field>
            </div>
          </div>
          <div className="flex justify-end">
            <Button
              type="submit"
              loading={
                priceTierContainerDiscountCreateMutation.isPending ||
                updateDerivedPriceList.isPending
              }
            >
              Create
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
